import React from 'react';
import { getIntl } from '../../../utils/intl';
import { APITypes, MedicalDataTypes } from '../../../primary-record-types';
import * as Card from '../../v2BaseComponents/Card';
import getDateString, { getDateSpanString } from '../../../utils/getDateString';
import { resourceTypeGuards } from '../../../utils/medicalDataUtils';
import { SearchInputRecord } from './ResourceListSearchFilters';
import SelectableMinimalCard, { NotSelectableProps, SelectableProps } from '../SelectableMinimalCard';
import { getDosageInstructions } from '../../../utils/getDosageInstructions';
import addressToString from '../../../utils/addressToString';
import LazyLoadedReferenceCounts, { LazyLoadedPhysician } from '../ResourceCard/LazyLoadedReferenceCounts';
import useMedicalProfile from '../../../hooks/api/useMedicalProfile';
import getName from '../../../utils/getName';
import Stack from '../../v2BaseComponents/Stack';
import LargeDateDisplay from '../LargeDateDisplay';
import { useResourceTitle } from '../../../utils/resourceUtils/getResourceTitle';

const intl = getIntl('resource-list-card', {
    dateAdded: 'Date added: {date}',
    startDate: 'Start date: {date}',
    status: 'Status',
    source: 'Source',
    ehrIntegration: 'Patient Portal',
    primaryRecord: 'Primary Record',
    createInPrimaryRecord: 'Created in Primary Record',
    resources: 'List',
    notes: 'Notes',
    active: 'Active',
    inactive: 'Inactive',
    dr: 'Dr. {name}',
});

export type ResourceListCardOwnProps = {
    resource: SearchInputRecord;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    profileId: string;
    includeProfileName?: boolean;
    onNavigateToDetails?: () => void;
    explanation?: string;
} & (SelectableProps | NotSelectableProps);

export type ResourceListCardProps = ResourceListCardOwnProps;

type RenderCardProps = {
    leftContent?: React.ReactNode;
    mainText: string | null;
    secondaryText?: string | null;
    subText: string | null;
    renderPhysician?: boolean;
    physicianFallback?: string | null;
    chipText?: string | null;
};
const ResourceListCard: React.FC<ResourceListCardProps> = (props) => {
    const { profile, isLoadingProfile } = useMedicalProfile({
        medicalProfileId: props.profileId,
    });
    const resourceTitle = useResourceTitle(props.resource, props.profileId);
    const getSourceText = () => {
        if (props.resource.metadata.source === '1uphealth') {
            return (props.resource.metadata as MedicalDataTypes.OneUpHealthResourceMetadata).healthSystemName;
        } else if (props.resource.metadata.source === 'fasten') {
            return (props.resource.metadata as MedicalDataTypes.FastenResourceMetadata).institutionName;
        }

        return intl('createInPrimaryRecord');
    };
    const sourceText = getSourceText();
    const renderCard = ({
        leftContent,
        mainText,
        secondaryText,
        subText,
        renderPhysician,
        physicianFallback,
        chipText,
    }: RenderCardProps) => (
        <SelectableMinimalCard key={props.resource.id} {...props}>
            <Stack vertical flex={1}>
                <Card.CardLeftContent leftContent={leftContent || null}>
                    <Card.CardRightContent
                        rightContent={
                            <Stack vertical fullHeight justify="flex-end" align="flex-end">
                                <LazyLoadedReferenceCounts
                                    resourceId={props.resource.id}
                                    medicalProfileId={props.profileId}
                                />
                            </Stack>
                        }
                    >
                        <Card.CardContent>
                            {mainText ? <Card.CardHeader>{mainText}</Card.CardHeader> : null}
                            {secondaryText ? <Card.CardBody>{secondaryText}</Card.CardBody> : null}
                            {chipText ? <Card.CardChip title={chipText} highlight /> : null}
                            {renderPhysician ? (
                                <LazyLoadedPhysician resourceId={props.resource.id} medicalProfileId={props.profileId}>
                                    {(physician) => {
                                        const text = physician ? intl('dr', { name: physician }) : physicianFallback;
                                        return text ? <Card.CardBody2>{text}</Card.CardBody2> : null;
                                    }}
                                </LazyLoadedPhysician>
                            ) : null}
                            {subText ? <Card.CardBody2>{subText}</Card.CardBody2> : null}
                            {props.includeProfileName && !isLoadingProfile && profile ? (
                                <Card.CardBody2>{getName(profile.contactDetails)}</Card.CardBody2>
                            ) : null}
                            {props.explanation ? <Card.CardBody2>{props.explanation}</Card.CardBody2> : null}
                        </Card.CardContent>
                    </Card.CardRightContent>
                </Card.CardLeftContent>
            </Stack>
        </SelectableMinimalCard>
    );

    const renderConditionCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordCondition>,
    ) => {
        return renderCard({
            mainText: resourceTitle,
            physicianFallback: sourceText,
            renderPhysician: true,
            subText: resource.startDateTime
                ? intl('startDate', { date: getDateString(resource.startDateTime, 'date') })
                : intl('dateAdded', { date: getDateString(resource.dateAdded, 'date') }),
        });
    };

    const renderAllergyCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordAllergy>,
    ) => {
        return renderCard({
            mainText: resourceTitle,
            secondaryText: resource.notes,
            subText: resource.startDateTime
                ? intl('startDate', { date: getDateString(resource.startDateTime, 'date') })
                : intl('dateAdded', { date: getDateString(resource.dateAdded, 'date') }),
        });
    };

    const renderMedicationCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordMedication>,
    ) => {
        return renderCard({
            mainText: resourceTitle,
            renderPhysician: true,
            physicianFallback: sourceText,
            subText: getDosageInstructions(resource),
            chipText: resource.isActive ? intl('active') : intl('inactive'),
        });
    };

    const renderVisitCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVisit>,
    ) => {
        return renderCard({
            leftContent: resource.startDateTime ? (
                <Stack vertical align="center" justify="center" fullHeight>
                    <LargeDateDisplay date={resource.startDateTime} />
                </Stack>
            ) : null,
            mainText: resourceTitle,
            secondaryText: addressToString(resource as any),
            subText: getDateSpanString(resource.startDateTime, resource.endDateTime, 'date-time') || null,
        });
    };

    const renderImagingCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordImaging>,
    ) => {
        return renderCard({
            mainText: resource.name,
            subText: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        });
    };

    const renderVaccinationCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordVaccination>,
    ) => {
        return renderCard({
            mainText: resourceTitle,
            subText: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        });
    };

    const renderProcedureCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordProcedure>,
    ) => {
        return renderCard({
            mainText: resourceTitle,
            subText: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        });
    };

    const renderLabCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordLab>,
    ) => {
        return renderCard({
            mainText: resourceTitle,
            subText: resource.dateTime ? getDateString(resource.dateTime, 'date') : null,
        });
    };

    if (resourceTypeGuards.isCondition(props.resource)) {
        return renderConditionCard(props.resource);
    } else if (resourceTypeGuards.isAllergy(props.resource)) {
        return renderAllergyCard(props.resource);
    } else if (resourceTypeGuards.isMedication(props.resource)) {
        return renderMedicationCard(props.resource);
    } else if (resourceTypeGuards.isVisit(props.resource)) {
        return renderVisitCard(props.resource);
    } else if (resourceTypeGuards.isImaging(props.resource)) {
        return renderImagingCard(props.resource);
    } else if (resourceTypeGuards.isVaccination(props.resource)) {
        return renderVaccinationCard(props.resource);
    } else if (resourceTypeGuards.isProcedure(props.resource)) {
        return renderProcedureCard(props.resource);
    } else if (resourceTypeGuards.isLab(props.resource)) {
        return renderLabCard(props.resource);
    }
};

export default React.memo(ResourceListCard);
